import type { MatterBreadcrumbProps } from "@/components/ui/MatterBreadcrumb.tsx/MatterBreadcrumb";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { MatterEvent, MatterNote, MatterTask } from "@/lib/types";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

type LinkedBreadcrumbItem = {
  matterId: string;
  incidentReportId?: string;
  lienId?: string;
  matterEmailId?: string;
  matterExpenseId?: string;
  matterFileId?: string;
  matterInsuranceId?: string;
  matterLitigationDateId?: string;
  matterLostWagesId?: string;
  matterNoteId?: string;
  matterPropertyDamageId?: string;
  matterSettlementCounterId?: string;
  matterSettlementDemandId?: string;
  matterSettlementOfferId?: string;
  matterSignatureRequestId?: string;
  matterStatuteOfLimitationsId?: string;
  matterTrustCheckId?: string;
  matterVehicleId?: string;
  medicalTreatmentAppointmentId?: string;
  medicalTreatmentBalanceConfirmationId?: string;
  medicalTreatmentBillId?: string;
  medicalTreatmentId?: string;
  medicalTreatmentPlanId?: string;
  medicalTreatmentRecordId?: string;
  taskId?: string;
};

const eventSubjectField: Record<string, keyof LinkedBreadcrumbItem> = {
  email: "matterEmailId",
  incident_report: "incidentReportId",
  matter_email: "matterEmailId",
  matter_expense: "matterExpenseId",
  matter_file: "matterFileId",
  matter_insurance: "matterInsuranceId",
  matter_lien: "lienId",
  matter_litigation_date: "matterLitigationDateId",
  matter_lost_wages: "matterLostWagesId",
  matter_note: "matterNoteId",
  matter_property_damage: "matterPropertyDamageId",
  matter_settlement_counter: "matterSettlementCounterId",
  matter_settlement_demand: "matterSettlementDemandId",
  matter_settlement_offer: "matterSettlementOfferId",
  matter_signature_request: "matterSignatureRequestId",
  matter_statute_of_limitations: "matterStatuteOfLimitationsId",
  matter_task: "taskId",
  matter_trust_check: "matterTrustCheckId",
  matter_vehicle: "matterVehicleId",
  medical_treatment: "medicalTreatmentId",
  medical_treatment_appointment: "medicalTreatmentAppointmentId",
  medical_treatment_balance_confirmation:
    "medicalTreatmentBalanceConfirmationId",
  medical_treatment_bill: "medicalTreatmentBillId",
  medical_treatment_plan: "medicalTreatmentPlanId",
  medical_treatment_record: "medicalTreatmentRecordId",
};

const getLinkedBreadcrumbItem = (
  item: MatterTask | MatterNote | MatterEvent,
): LinkedBreadcrumbItem | undefined => {
  const linkedItem: LinkedBreadcrumbItem = { matterId: item.matterId };

  if ("primarySubjectType" in item || "actionType" in item) {
    const event = item;
    const field = event.primarySubjectType
      ? eventSubjectField[event.primarySubjectType]
      : undefined;
    if (!field || !event.primarySubjectId) {
      return undefined;
    }
    return { ...linkedItem, [field]: event.primarySubjectId };
  }

  const relations = item as Record<string, unknown>;
  const getRelationId = (...keys: string[]) => {
    const value = keys
      .map((key) => relations[key])
      .find((relation) => typeof relation === "string" && relation.length > 0);
    return typeof value === "string" ? value : undefined;
  };
  const linkedFields: Array<
    [keyof LinkedBreadcrumbItem, string | null | undefined]
  > = [
    ["matterFileId", getRelationId("fileId")],
    ["matterInsuranceId", getRelationId("insuranceId")],
    ["matterVehicleId", getRelationId("vehicleId")],
    ["matterPropertyDamageId", getRelationId("propertyDamageId")],
    ["incidentReportId", getRelationId("incidentReportId")],
    ["lienId", getRelationId("lienId")],
    ["matterSettlementDemandId", getRelationId("settlementDemandId")],
    ["matterSettlementOfferId", getRelationId("settlementOfferId")],
    ["matterSettlementCounterId", getRelationId("settlementCounterId")],
    ["matterExpenseId", getRelationId("expenseId")],
    ["matterLostWagesId", getRelationId("lostWagesId")],
    ["medicalTreatmentId", getRelationId("treatmentId", "medicalTreatmentId")],
    [
      "medicalTreatmentAppointmentId",
      getRelationId("treatmentAppointmentId", "medicalTreatmentAppointmentId"),
    ],
    [
      "medicalTreatmentBillId",
      getRelationId("treatmentBillId", "medicalTreatmentBillId"),
    ],
    ["medicalTreatmentRecordId", getRelationId("medicalTreatmentRecordId")],
    ["medicalTreatmentPlanId", getRelationId("medicalTreatmentPlanId")],
    [
      "medicalTreatmentBalanceConfirmationId",
      getRelationId("medicalTreatmentBalanceConfirmationId"),
    ],
    ["matterTrustCheckId", getRelationId("trustCheckId")],
    ["matterLitigationDateId", getRelationId("litigationDateId")],
    ["matterSignatureRequestId", getRelationId("signatureRequestId")],
    ["matterStatuteOfLimitationsId", getRelationId("statuteOfLimitationsId")],
    ["matterEmailId", getRelationId("emailId")],
  ];

  const linkedField = linkedFields.find(([, value]) => Boolean(value));
  return linkedField
    ? { ...linkedItem, [linkedField[0]]: linkedField[1] ?? undefined }
    : undefined;
};

export const useGetSubitemsForBreadcrumbs = () => {
  const navigate = useNavigate();
  const {
    openExpenseModal,
    openInsuranceModal,
    openIncidentReportModal,
    openLienModal,
    openLostWageModal,
    openMedicalTreatmentModal,
    openPropertyDamageModal,
    openSettlementDemandModal,
    openSettlementCounterModal,
    openTreatmentAppointmentModal,
    openTreatmentBillModal,
    openTreatmentRecordModal,
    openVehicleModal,
    openTrustCheckModal,
    openTreatmentPlanModal,
    openSignatureRequestModal,
    openLitigationDateModal,
    openMatterEmailViewModal,
    openTaskModal,
    openFileModal,
  } = useGlobalModal();

  const getSubitemsForBreadcrumbs = useCallback(
    (
      source: MatterTask | MatterNote | MatterEvent,
      disableNavigation?: boolean,
    ) => {
      const appendLinkedBreadcrumbs = (
        breadcrumbs: MatterBreadcrumbProps["items"],
      ): MatterBreadcrumbProps["items"] => breadcrumbs;
      if ("phaseActionId" in source && source.phaseActionId) {
        const context = source.phaseAction;
        return [
          {
            id: source.phaseActionId,
            type: "phase-action" as const,
            title: context
              ? `${context.phaseTitle} / ${context.title}${context.isCurrent ? "" : " (inactive)"}`
              : "Phase action",
            disableNavigation: disableNavigation || !context?.isCurrent,
            onClick: () => {
              void navigate({
                to: "/matters/$matterId",
                params: { matterId: source.matterId },
                search: { phaseActionId: source.phaseActionId ?? undefined },
              });
            },
          },
        ];
      }
      const item = getLinkedBreadcrumbItem(source);

      if (!item) {
        return [];
      }
      if (item.taskId) {
        return appendLinkedBreadcrumbs([
          {
            id: `task-${item.taskId}`,
            type: "task" as const,
            title: "Task",
            disableNavigation,
            onClick: () => {
              openTaskModal({
                matterId: item.matterId,
                taskId: item.taskId,
                editing: true,
                initialTab: "details",
              });
            },
          },
        ]);
      }
      if (item.matterNoteId) {
        return appendLinkedBreadcrumbs([
          {
            id: `note-${item.matterNoteId}`,
            type: "note" as const,
            title: "Note",
            disableNavigation,
          },
        ]);
      }
      if (item.matterFileId) {
        return appendLinkedBreadcrumbs([
          {
            id: `file-${item.matterFileId}`,
            type: "file" as const,
            title: "File",
            disableNavigation,
            onClick: () => {
              openFileModal({
                matterId: item.matterId,
                activeFileId: item.matterFileId,
                currentFolderId: "",
                isFolder: false,
                editing: true,
              });
            },
          },
        ]);
      }
      if (item.matterInsuranceId) {
        return appendLinkedBreadcrumbs([
          {
            id: "insurance",
            type: "insurance" as const,
            title: "Insurance",
            disableNavigation,
            onClick: () => {
              openInsuranceModal({
                matterId: item.matterId,
                insuranceId: item.matterInsuranceId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/matter-details/$tab",
                params: { matterId: item.matterId, tab: "client" },
                search: { insuranceId: item.matterInsuranceId },
              }).then(() => {
                openInsuranceModal({
                  matterId: item.matterId,
                  insuranceId: item.matterInsuranceId,
                  editing: true,
                  initialTab: "details",
                });
              });
            },
          },
        ]);
      }
      if (item.matterVehicleId) {
        return appendLinkedBreadcrumbs([
          {
            id: "vehicle",
            type: "vehicle" as const,
            title: "Vehicle",
            disableNavigation,
            onClick: () => {
              openVehicleModal({
                matterId: item.matterId,
                vehicleId: item.matterVehicleId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/matter-details/$tab",
                params: { matterId: item.matterId, tab: "client" },
                search: { vehicleId: item.matterVehicleId },
              }).then(() => {
                openVehicleModal({
                  matterId: item.matterId,
                  vehicleId: item.matterVehicleId,
                  editing: true,
                  initialTab: "details",
                });
              });
            },
          },
        ]);
      }
      if (item.matterPropertyDamageId) {
        return appendLinkedBreadcrumbs([
          {
            id: "propertyDamage",
            type: "property-damage" as const,
            title: "Property damage",
            disableNavigation,
            onClick: () => {
              openPropertyDamageModal({
                matterId: item.matterId,
                propertyDamageId: item.matterPropertyDamageId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/property-damage",
                params: { matterId: item.matterId },
                search: {
                  propertyDamageId: item.matterPropertyDamageId,
                },
              });
            },
          },
        ]);
      }
      if (item.incidentReportId) {
        return appendLinkedBreadcrumbs([
          {
            id: "incidentReport",
            type: "incident-report" as const,
            title: "Incident report",
            disableNavigation,
            onClick: () => {
              openIncidentReportModal({
                matterId: item.matterId,
                incidentReportId: item.incidentReportId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/matter-details/$tab",
                params: { matterId: item.matterId, tab: "client" },
              }).then(() => {
                openIncidentReportModal({
                  matterId: item.matterId,
                  incidentReportId: item.incidentReportId,
                  editing: true,
                  initialTab: "details",
                });
              });
            },
          },
        ]);
      }
      if (item.lienId) {
        return appendLinkedBreadcrumbs([
          {
            id: "lien",
            type: "lien" as const,
            title: "Lien",
            disableNavigation,
            onClick: () => {
              openLienModal({
                matterId: item.matterId,
                lienId: item.lienId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/liens",
                params: { matterId: item.matterId },
                search: {
                  lienId: item.lienId,
                },
              });
            },
          },
        ]);
      }
      if (item.matterSettlementDemandId) {
        return appendLinkedBreadcrumbs([
          {
            id: "settlementDemand",
            type: "settlement-demand" as const,
            title: "Settlement demand",
            disableNavigation,
            onClick: () => {
              openSettlementDemandModal({
                matterId: item.matterId,
                settlementId: item.matterSettlementDemandId,
                editing: true,
                initialTab: "details",
                negotiationType: "Demand",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/settlements",
                params: { matterId: item.matterId },
                search: {
                  settlementDemandId: item.matterSettlementDemandId,
                },
              });
            },
          },
        ]);
      }
      if (item.matterSettlementOfferId) {
        return appendLinkedBreadcrumbs([
          {
            id: "settlementOffer",
            type: "settlement-offer" as const,
            title: "Settlement offer",
            disableNavigation,
            onClick: () => {
              openSettlementDemandModal({
                matterId: item.matterId,
                settlementId: item.matterSettlementOfferId,
                editing: true,
                initialTab: "details",
                negotiationType: "Offer",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/settlements",
                params: { matterId: item.matterId },
                search: {
                  settlementDemandId: item.matterSettlementOfferId,
                },
              });
            },
          },
        ]);
      }
      if (item.matterSettlementCounterId) {
        return appendLinkedBreadcrumbs([
          {
            id: "settlementCounter",
            type: "settlement-counter" as const,
            title: "Settlement counter",
            disableNavigation,
            onClick: () => {
              openSettlementCounterModal({
                matterId: item.matterId,
                settlementCounterId: item.matterSettlementCounterId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/settlements",
                params: { matterId: item.matterId },
                search: {
                  settlementDemandId: item.matterSettlementCounterId,
                },
              });
            },
          },
        ]);
      }
      if (item.matterExpenseId) {
        return appendLinkedBreadcrumbs([
          {
            id: "expense",
            type: "expense" as const,
            title: "Cost",
            disableNavigation,
            onClick: () => {
              openExpenseModal({
                matterId: item.matterId,
                expenseId: item.matterExpenseId,
                editing: true,
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/costs",
                params: { matterId: item.matterId },
                search: {
                  expenseId: item.matterExpenseId,
                },
              }).then(() => {
                openExpenseModal({
                  matterId: item.matterId,
                  expenseId: item.matterExpenseId,
                  editing: true,
                });
              });
            },
          },
        ]);
      }
      if (item.matterLostWagesId) {
        return appendLinkedBreadcrumbs([
          {
            id: "lostWage",
            type: "lost-wage" as const,
            title: "Lost wage",
            disableNavigation,
            onClick: () => {
              openLostWageModal({
                matterId: item.matterId,
                lostWageId: item.matterLostWagesId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/matter-details/$tab",
                params: { matterId: item.matterId, tab: "client" },
                search: {
                  lostWageId: item.matterLostWagesId,
                },
              });
            },
          },
        ]);
      }
      if (item.medicalTreatmentAppointmentId) {
        return appendLinkedBreadcrumbs([
          {
            id: item.medicalTreatmentAppointmentId,
            type: "medical-treatment-appointment" as const,
            title: "Treatment date",
            disableNavigation,
            onClick: () => {
              openTreatmentAppointmentModal({
                matterId: item.matterId,
                appointmentId: item.medicalTreatmentAppointmentId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: {
                  appointmentId: item.medicalTreatmentAppointmentId,
                },
              }).then(() => {
                openTreatmentAppointmentModal({
                  matterId: item.matterId,
                  appointmentId: item.medicalTreatmentAppointmentId,
                  editing: true,
                  initialTab: "details",
                });
              });
            },
          },
        ]);
      }
      if (item.medicalTreatmentBillId) {
        return appendLinkedBreadcrumbs([
          {
            id: item.medicalTreatmentBillId,
            type: "medical-treatment-bill" as const,
            title: "Medical treatment bill",
            disableNavigation,
            onClick: () => {
              openTreatmentBillModal({
                matterId: item.matterId,
                billId: item.medicalTreatmentBillId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: { billId: item.medicalTreatmentBillId },
              }).then(() => {
                openTreatmentBillModal({
                  matterId: item.matterId,
                  billId: item.medicalTreatmentBillId,
                  editing: true,
                  initialTab: "details",
                });
              });
            },
          },
        ]);
      }
      if (item.medicalTreatmentRecordId) {
        return appendLinkedBreadcrumbs([
          {
            id: item.medicalTreatmentRecordId,
            type: "medical-treatment-record" as const,
            title: "Medical treatment record",
            disableNavigation,
            onClick: () => {
              openTreatmentRecordModal({
                matterId: item.matterId,
                recordId: item.medicalTreatmentRecordId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: { recordId: item.medicalTreatmentRecordId },
              }).then(() => {
                openTreatmentRecordModal({
                  matterId: item.matterId,
                  recordId: item.medicalTreatmentRecordId,
                  editing: true,
                  initialTab: "details",
                });
              });
            },
          },
        ]);
      }
      if (item.medicalTreatmentPlanId) {
        return appendLinkedBreadcrumbs([
          {
            id: item.medicalTreatmentPlanId,
            type: "medical-treatment-plan" as const,
            title: "Treatment plan",
            disableNavigation,
            onClick: () => {
              openTreatmentPlanModal({
                matterId: item.matterId,
                planId: item.medicalTreatmentPlanId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: { planId: item.medicalTreatmentPlanId },
              });
            },
          },
        ]);
      }
      if (item.medicalTreatmentId) {
        const returnObject: MatterBreadcrumbProps["items"] = [
          {
            id: "medicalTreatment",
            type: "medical-treatment" as const,
            title: "Medical treatment",
            disableNavigation,
            onClick: () => {
              openMedicalTreatmentModal({
                matterId: item.matterId,
                treatmentId: item.medicalTreatmentId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: {
                  medicalTreatmentId: item.medicalTreatmentId,
                },
              });
            },
          },
        ];
        if (item.medicalTreatmentAppointmentId) {
          returnObject.push({
            id: item.medicalTreatmentAppointmentId,
            type: "medical-treatment-appointment" as const,
            title: "Treatment date",
            disableNavigation,
            onClick: () => {
              openTreatmentAppointmentModal({
                matterId: item.matterId,
                appointmentId: item.medicalTreatmentAppointmentId,
                treatmentId: item.medicalTreatmentId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: {
                  appointmentId: item.medicalTreatmentAppointmentId,
                },
              });
            },
          });
        }
        if (item.medicalTreatmentBillId) {
          returnObject.push({
            id: "bill",
            type: "medical-treatment-bill" as const,
            title: "Bill",
            disableNavigation,
            onClick: () => {
              openTreatmentBillModal({
                matterId: item.matterId,
                billId: item.medicalTreatmentBillId,
                treatmentId: item.medicalTreatmentId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: {
                  billId: item.medicalTreatmentBillId,
                },
              });
            },
          });
        }
        if (item.medicalTreatmentRecordId) {
          returnObject.push({
            id: "record",
            type: "medical-treatment-record" as const,
            title: "Record",
            disableNavigation,
            onClick: () => {
              openTreatmentRecordModal({
                matterId: item.matterId,
                recordId: item.medicalTreatmentRecordId,
                treatmentId: item.medicalTreatmentId,
                editing: true,
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: {
                  recordId: item.medicalTreatmentRecordId,
                },
              });
            },
          });
        }
        if (item.medicalTreatmentPlanId) {
          returnObject.push({
            id: "plan",
            type: "medical-treatment-plan" as const,
            title: "Treatment plan",
            disableNavigation,
            onClick: () => {
              openTreatmentPlanModal({
                matterId: item.matterId,
                planId: item.medicalTreatmentPlanId,
                treatmentId: item.medicalTreatmentId,
                editing: true,
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/medical-treatments",
                params: { matterId: item.matterId },
                search: {
                  planId: item.medicalTreatmentPlanId,
                },
              });
            },
          });
        }
        if (item.medicalTreatmentBalanceConfirmationId) {
          returnObject.push({
            id: "balanceConfirmation",
            type: "medical-treatment-balance-confirmation" as const,
            title: "Balance confirmation",
            disableNavigation,
          });
        }
        return appendLinkedBreadcrumbs(returnObject);
      }
      if (item.medicalTreatmentBalanceConfirmationId) {
        return appendLinkedBreadcrumbs([
          {
            id: "balanceConfirmation",
            type: "medical-treatment-balance-confirmation" as const,
            title: "Balance confirmation",
            disableNavigation,
          },
        ]);
      }
      if (item.matterTrustCheckId) {
        return appendLinkedBreadcrumbs([
          {
            id: "trustCheck",
            type: "trust-check" as const,
            title: "Trust check",
            disableNavigation,
            onClick: () => {
              openTrustCheckModal({
                matterId: item.matterId,
                trustCheckId: item.matterTrustCheckId,
                editing: true,
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/settlements",
                params: { matterId: item.matterId },
                search: {
                  trustCheckId: item.matterTrustCheckId,
                },
              });
            },
          },
        ]);
      }
      if (item.matterSignatureRequestId) {
        const signatureRequestId = item.matterSignatureRequestId;
        return appendLinkedBreadcrumbs([
          {
            id: signatureRequestId,
            type: "signature-request" as const,
            title: "Signature request",
            disableNavigation,
            onClick: () => {
              openSignatureRequestModal({
                matterId: item.matterId,
                signatureRequestId,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/signature-requests",
                params: { matterId: item.matterId },
              }).then(() => {
                openSignatureRequestModal({
                  matterId: item.matterId,
                  signatureRequestId,
                  initialTab: "details",
                });
              });
            },
          },
        ]);
      }
      if (item.matterLitigationDateId) {
        return appendLinkedBreadcrumbs([
          {
            id: item.matterLitigationDateId,
            type: "matter-litigation-date" as const,
            title: "Litigation date",
            disableNavigation,
            onClick: () => {
              openLitigationDateModal({
                matterId: item.matterId,
                litigationDateId: item.matterLitigationDateId,
                editing: true,
                initialTab: "details",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/litigation",
                params: { matterId: item.matterId },
                search: {
                  litigationDateId: item.matterLitigationDateId,
                },
              });
            },
          },
        ]);
      }
      if (item.matterStatuteOfLimitationsId) {
        return appendLinkedBreadcrumbs([
          {
            id: item.matterStatuteOfLimitationsId,
            type: "statute-of-limitations" as const,
            title: "Statute of Limitations",
            disableNavigation,
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/matter-details/$tab",
                params: { matterId: item.matterId, tab: "defendant" },
                search: {
                  statuteOfLimitationsId: item.matterStatuteOfLimitationsId,
                },
              });
            },
          },
        ]);
      }
      if (item.matterEmailId) {
        return appendLinkedBreadcrumbs([
          {
            id: item.matterEmailId,
            type: "email" as const,
            title: "Email",
            disableNavigation,
            onClick: () => {
              openMatterEmailViewModal({
                matterId: item.matterId,
                matterEmailId: item.matterEmailId ?? "",
              });
            },
            onNavigateClick: () => {
              void navigate({
                to: "/matters/$matterId/emails",
                params: { matterId: item.matterId },
                search: {
                  emailId: item.matterEmailId,
                },
              });
              openMatterEmailViewModal({
                matterId: item.matterId,
                matterEmailId: item.matterEmailId ?? "",
              });
            },
          },
        ]);
      }
      return appendLinkedBreadcrumbs([]);
    },
    [
      navigate,
      openExpenseModal,
      openInsuranceModal,
      openLienModal,
      openLostWageModal,
      openMedicalTreatmentModal,
      openPropertyDamageModal,
      openIncidentReportModal,
      openSettlementDemandModal,
      openSettlementCounterModal,
      openSignatureRequestModal,
      openTreatmentAppointmentModal,
      openTreatmentBillModal,
      openTreatmentRecordModal,
      openTreatmentPlanModal,
      openVehicleModal,
      openTrustCheckModal,
      openLitigationDateModal,
      openFileModal,
      openTaskModal,
      openMatterEmailViewModal,
    ],
  );

  return getSubitemsForBreadcrumbs;
};
